<template>
  <d-container
    fluid
    :class="{
      'main-content-container': true,
      'px-4': !agentID,
      'px-0': agentID
    }">
    <div class="page-header-with-filters">
      <!-- Page Header -->
      <d-row
        v-if="!agentID"
        no-gutters
        class="page-header py-4">
        <d-col
          col
          sm="6"
          class="text-center text-sm-left mb-4 mb-sm-0">
          <span class="text-uppercase page-subtitle">
            {{ $t('customer.customers') }}
          </span>
          <h3 class="page-title">
            {{ $t('customer.customersList') }}
          </h3>
        </d-col>
        <d-col
          col
          sm="6"
          class="text-right text-sm-right d-flex justify-content-end align-items-end"
          style="gap: 0.75rem;">
          <d-button
            theme="primary"
            outline
            :disabled="exportInProgress || !loaded"
            @click.prevent="exportCustomers">
            <div
              v-if="exportInProgress"
              class="spinner-border spinner-border-sm mr-1"
              role="status">
            </div>
            <i
              v-else
              class="material-icons mr-1">
              cloud_download
            </i>
            {{ $t('customer.exportCustomers') }}
          </d-button>

          <d-button
            theme="success"
            @click.prevent="$router.push('/klienci/nowy')">
            <i class="material-icons">add</i>
            {{ $t('customer.addNewCustomer') }}
          </d-button>
        </d-col>
      </d-row>

      <list-filters
        :config="filtersConfig"
        :isListLoaded="allDataLoaded"
        :view-name="'customers'"
        @init="handleFiltersInit"
        @search="handleFiltersSearch" />
    </div>

    <d-card style="width: 100%;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th
              scope="col"
              class="border-0">{{ $t('customer.companyName') }}</th>
            <th
              scope="col"
              class="border-0">{{ $t('settings.country.country') }}</th>
            <th
              scope="col"
              class="border-0"
              style="min-width: 200px;">{{ $t('customer.contact.contact') }}</th>
            <th
              scope="col"
              class="border-0"
              style="min-width: 200px;">{{ $t('customer.statistics') }}</th>
            <th
              scope="col"
              class="border-0">{{ $t('ui.actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(customer, index) of customers"
            :key="'customer-' + index">
            <td class="align-middle">
              {{ customer.name }}
              <br v-if="customer.is_supplier">
              <d-badge
                v-if="customer.is_supplier"
                theme="warning">
                {{ $t('ui.supplierAlt') }}
              </d-badge>
            </td>
            <td class="align-middle">
              {{ getCountry(customer.country_id) }}
            </td>
            <td class="align-middle">
              <small>
                {{ $t('customer.phoneShortCap') }}: {{ customer.phone }}<br>
                {{ $t('ui.eMailCap') }}: {{ customer.email }}<br>
                {{ $t('customer.newsletterAgreement') }}:
                <span
                  :class="{
                    'text-success': customer.newsletter_status === 'SUBSCRIBED',
                    'text-danger': customer.newsletter_status === 'UNSUBSCRIBED'
                  }">
                  <template v-if="customer.newsletter_status === 'SUBSCRIBED'">
                    {{ $t('customer.newsletterStatus.subscribed') }}
                  </template>
                  <template v-if="customer.newsletter_status === 'UNSUBSCRIBED'">
                    {{ $t('customer.newsletterStatus.unsubscribed') }}
                  </template>
                  <template v-if="customer.newsletter_status === null">
                    {{ $t('customer.newsletterStatus.null') }}
                  </template>
                </span>
              </small>
            </td>
            <td class="align-middle">
              <small>
                {{ $t('customer.ordersAndOffers') }}: {{ customer.orders_count }}<br>
                <template v-if="customer.progress_orders_count === 0">
                  {{ $t('customer.ordersInProgress') }}: {{ customer.progress_orders_count }}<br>
                </template>
                <strong
                  v-else
                  class="text-success">
                  {{ $t('customer.ordersInProgress') }}: {{ customer.progress_orders_count }}<br>
                </strong>
                <template v-if="customer.progress_offers_count === 0">
                  {{ $t('customer.offersInProgress') }}: {{ customer.progress_offers_count }}<br>
                </template>
                <strong
                  v-else
                  class="text-primary">
                  {{ $t('customer.offersInProgress') }}: {{ customer.progress_offers_count }}<br>
                </strong>
                <template v-if="customer.unpaid_settlements_count === 0">
                  {{ $t('customer.unsettledInvoices') }}: {{ customer.unpaid_settlements_count }}
                </template>
                <strong
                  v-else
                  class="text-danger">
                  {{ $t('customer.unsettledInvoices') }}: {{ customer.unpaid_settlements_count }}
                </strong>
              </small>
            </td>
            <td
              class="align-middle"
              style="width: 150px;">
              <d-button
                v-if="!agentID"
                theme="primary"
                @click.native="$router.push('/klienci/edycja/' + customer.id)"
                class="mr-2"
                :title="$t('ui.edit')">
                <i class="material-icons">edit</i>
              </d-button>
              <d-button
                v-if="!agentID && userIsAdmin"
                theme="danger"
                :disabled="removeInProgress"
                @click.native="removeCustomer(customer.id)"
                :title="$t('ui.delete')">
                <i class="material-icons">delete</i>
              </d-button>
              <d-button
                v-if="agentID"
                theme="info"
                @click.prevent.native="openCustomerInNewTab(customer.id)"
                class="mr-2"
                :title="$t('ui.edit')">
                <i class="material-icons">open_in_new</i>
              </d-button>
            </td>
          </tr>
          <tr v-if="customers.length === 0">
            <td
              colspan="8"
              class="text-center">
              {{ $t('customer.noCustomersToDisplay') }}&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <div
        v-if="!allDataLoaded"
        class="pb-3 pt-3 text-center overlay-loading">
        {{ $t('ui.dataLoading') }}&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        {{ $t('ui.dataLoadingFailed') }} <a
          href="javascript:window.location.reload();"
        class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
      </d-alert>
    </d-card>

    <pagination
      :meta-data="paginationMetaData"
      :initial-page="currentPage" />
  </d-container>
</template>

<script>
import debounce from 'lodash.debounce';
import CommonGetters from './../utils/CommonGetters.js';
import FormUtils from './../utils/FormUtils.js';
import HasPagination from './../mixins/HasPagination.vue';
import ListUtils from './../utils/ListUtils.js';
import Pagination from './../components/common/Pagination.vue';
import cloneDeep from 'lodash.clonedeep';
import Vue from 'vue';

export default {
  name: 'customers',
  mixins: [
    HasPagination
  ],
  props: {
    agentID: {
      type: Number,
      required: false,
      default: 0
    }
  },
  components: {
    'pagination': Pagination
  },
  computed: {
    allDataLoaded () {
      return this.loaded && this.loadedCountries;
    },
    progressOrdersOptions () {
      return [
        {
          text: this.$t('customer.withoutOrdersInProgress'),
          value: 0
        },
        {
          text: this.$t('customer.withOrdersInProgress'),
          value: 1
        }
      ];
    },
    unpaidSettlementsOptions () {
      return [
        {
          text: this.$t('customer.withoutUnsettledInvoices'),
          value: 0
        },
        {
          text: this.$t('customer.withUnsettledInvoices'),
          value: 1
        }
      ];
    },
    countriesOptions () {
      return this.countries.map(country => ({
        text: country.name,
        value: country.id
      }));
    },
    userIsAdmin () {
      return this.$store.getters.getUserType === 'admin';
    },
    filtersConfig () {
      return [
        {
          key: 'search',
          apiKey: 'search',
          label: this.$t('ui.search'),
          placeholder: this.$t('customer.enterCustomerData'),
          type: 'text',
          isBasic: true,
          defaultValue: ''
        },
        {
          key: 'country_id',
          apiKey: 'country_id',
          label: this.$t('customer.customerCountryQuestionMark'),
          placeholder: this.$t('customer.customerCountryQuestionMark'),
          type: 'select',
          isBasic: true,
          defaultValue: null,
          options: this.countriesOptions
        },
        {
          key: 'city',
          apiKey: 'city',
          label: this.$t('customer.enterCustomerAddress'),
          placeholder: this.$t('customer.enterCustomerAddress'),
          type: 'text',
          isBasic: true,
          defaultValue: '',
          description: this.$t('customer.filters.addressDesc')
        },
        {
          key: 'agent_id',
          apiKey: 'agent_id',
          label: 'Agent',
          type: 'agentSelect',
          isBasic: true,
          defaultValue: null,
          permission: () => !this.agentID
        },
        {
          key: 'tags',
          label: this.$t('customer.customerTags'),
          placeholder: this.$t('customer.customerTags'),
          type: 'select',
          multiple: true,
          isBasic: false,
          defaultValue: [],
          options: this.customerTags,
          optionLabel: 'name',
          reduce: option => option,
          apiTransform: (val, where) => {
            where['tags'] = val.map(tagData => tagData.id);
          }
        },
        {
          key: 'progressOrders',
          apiKey: 'progress_orders',
          label: this.$t('customer.isOrdersInRpogress'),
          placeholder: this.$t('customer.isOrdersInRpogress'),
          type: 'select',
          isBasic: false,
          defaultValue: null,
          options: this.progressOrdersOptions
        },
        {
          key: 'unpaidSettlements',
          apiKey: 'unpaid_settlements',
          label: this.$t('customer.isInvoicesNotSettled'),
          placeholder: this.$t('customer.isInvoicesNotSettled'),
          type: 'select',
          isBasic: false,
          defaultValue: null,
          options: this.unpaidSettlementsOptions
        },
        {
          key: 'discount_type',
          apiKey: 'discount_type',
          label: this.$t('customer.selectDiscountType'),
          placeholder: this.$t('customer.selectDiscountType'),
          type: 'select',
          isBasic: false,
          defaultValue: '',
          permission: () => !this.agentID,
          options: [
            { text: this.$t('customer.selectDiscountType'), value: '' },
            { text: this.$t('customer.selectDiscountTypes.discountProducts'), value: 'discount_products' },
            { text: this.$t('customer.selectDiscountTypes.discountServices'), value: 'discount_services' },
            { text: this.$t('customer.selectDiscountTypes.discountShipping'), value: 'discount_shipping' }
          ]
        },
        {
          key: 'discount_from',
          apiKey: 'discount_from',
          label: this.$t('customer.filters.discountFrom'),
          placeholder: '0',
          type: 'number',
          min: 0,
          max: 100,
          isBasic: false,
          defaultValue: '',
          permission: () => !this.agentID,
          visible: (filters) => !!filters.discount_type,
          rules: (filters) => 'min_value:0|max_value:' + (filters.discount_to || 100),
          apiTransform: (val, where) => {
            where['discount_from'] = parseFloat(val);
          }
        },
        {
          key: 'discount_to',
          apiKey: 'discount_to',
          label: this.$t('customer.filters.discountTo'),
          placeholder: '100',
          type: 'number',
          min: 0,
          max: 100,
          isBasic: false,
          defaultValue: '',
          permission: () => !this.agentID,
          visible: (filters) => !!filters.discount_type,
          rules: (filters) => 'min_value:' + (filters.discount_from || 0) + '|max_value:100',
          apiTransform: (val, where) => {
            where['discount_to'] = parseFloat(val);
          }
        }
      ];
    }
  },
  data () {
    return {
      countries: [],
      customers: [],
      customerTags: [],
      loaded: false,
      loadedCountries: false,
      loadedCustomerTags: false,
      loadError: false,
      removeInProgress: false,
      exportInProgress: false,
      activeFilters: {},
      apiQueryPayload: {}
    };
  },
  mounted () {
    this.loadAdditionalData();
  },
  methods: {
    handleFiltersInit (filters, apiPayload) {
      this.activeFilters = filters;
      this.apiQueryPayload = apiPayload;

      this.loadFilteredData();
    },
    handleFiltersSearch (filters, apiPayload) {
      this.activeFilters = filters;
      this.apiQueryPayload = apiPayload;

      this.$bus.$emit('pagination-reset');
      this.debouncedLoadFilteredData();
    },
    debouncedLoadFilteredData: debounce(function () {
      this.loadFilteredData();
    }, 250),
    loadFilteredData () {
      let where = cloneDeep(this.apiQueryPayload) || {};

      if (this.agentID) {
        where.agent_id = this.agentID;
      }

      ListUtils.loadPaginatedItemsData(this, {
        method: 'get',
        endpoint: '/customers/items',
        listField: 'customers',
        params: {
          page: this.currentPage,
          where
        }
      });
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/countries/items',
        loadedKey: 'loadedCountries',
        outputKey: 'countries',
        errorKey: 'loadError'
      });

      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/customers/tags/items',
        loadedKey: 'loadedCustomerTags',
        outputKey: 'customerTags',
        errorKey: 'loadError',
        params: {
          return: 'get'
        }
      });
    },
    removeCustomer (customerID) {
      this.removeInProgress = true;

      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: '/customers/delete/',
        id: customerID,
        successTitle: this.$t('customer.removeCustomerSuccessTitle'),
        successText: this.$t('customer.removeCustomerSuccessText'),
        confirmTitle: this.$t('customer.removeCustomerConfirmTitle'),
        confirmText: this.$t('ui.deleteConfirmText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('customer.removeCustomerErrorText'),
        successAction: () => {
          this.removeInProgress = false;
          this.loadFilteredData();
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    },
    getCountry (countryID) {
      return CommonGetters.getCountryById(countryID, this.countries);
    },
    exportCustomers () {
      this.exportInProgress = true;

      this.$http.post('/customers/export', {}, {
        responseType: 'blob'
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', this.$t('customer.customerLowCase') + '.xlsx');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.exportInProgress = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.exportErrorTitle'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        this.exportInProgress = false;
      });
    },
    openCustomerInNewTab (customerID) {
      window.open(this.$router.resolve('/klienci/edycja/' + customerID).href, '_blank');
    }
  }
};
</script>
